import { Context, createContext, useContext } from 'react';
import getKey from './randomHeaderGenerator';
import {
	ApolloClient,
	ApolloLink,
	from,
	HttpLink,
	InMemoryCache,
	IntrospectionFragmentMatcher,
	NormalizedCacheObject
} from 'apollo-boost';
import * as Cookie from 'js-cookie';

import { captureErrorSentry, getLocale, isRequireCurrencyParam, STAY_LOGGED_IN } from './utils';
import introspectionResult from './fragmentTypes';
import './custom-event-polyfill';
import { CUSTOM_SCHEMAS_URL, get, getCurrency, GRAPHQL_KEY } from './cache';
import { Currency } from './enums';
import { v4 as uuidv4 } from 'uuid';
import ignoredErrorsLink from './ingoredErrorsLink';
import paximizerUTMLink from './paximizerUTMLink';
import WebskyAnalyticsApollo from './DataLayerAnalytics/WebskyAnalyticsApollo';
import getAffiliateData from '@shared/lib/affiliateData/getAffiliateData';
import { errorLink } from '@shared/api/apollo';

let token = Cookie.get(GRAPHQL_KEY);
let client: ApolloClient<NormalizedCacheObject>;

const urlFromLocalStorage = process.env.ACCEPT_CUSTOM_SCHEMAS_URL === 'true' ? get<string>(CUSTOM_SCHEMAS_URL) : null;

let GRAPHQL_URL = urlFromLocalStorage ?? process.env.API_URL ?? 'https://test.graphql.mlsd.ru/nemox/graphql/query/nemo';
let GraphQLClientContext: Context<ApolloClient<NormalizedCacheObject>>;

export const setApiUrl = (newApiUrl: string) => {
	if (!urlFromLocalStorage) {
		GRAPHQL_URL = newApiUrl;
	}
};

const webskyAnalyticsLink = new ApolloLink((operation, forward) => {
	return forward(operation).map(response => {
		try {
			WebskyAnalyticsApollo.analyze(operation, response, client);
		} catch (e) {
			console.error('Error in webskyAnalyticsLink', e);
			captureErrorSentry(`Error in webskyAnalyticsLink: ${e}`);
		}

		return response;
	});
});

const createOrderAffiliateLink = new ApolloLink((operation, forward) => {
	if (operation.operationName === 'CreateOrder' && !!operation.variables?.params) {
		const affiliateParams = getAffiliateData();

		if (affiliateParams) {
			operation.variables = {
				...operation.variables,
				params: {
					...operation.variables.params,
					lastRedirectSource: affiliateParams.redirectSource,
					lastRedirectId: affiliateParams.redirectId
				}
			};
		}
	}

	return forward(operation);
});

export const createClient = () => {
	const isDemo = document.location.href.includes('demo.websky.tech');

	const needToSendCurrencyInHeaders = !isRequireCurrencyParam();

	if (!client) {
		const fragmentMatcher = new IntrospectionFragmentMatcher({
			introspectionQueryResultData: introspectionResult
		});

		const getTokenLink = new ApolloLink((operation, forward) => {
			const context = operation.getContext();

			let lang = getLocale();

			if (context.lang) {
				lang = context.lang;
			}

			let currency = getCurrency();

			if (isDemo) {
				currency = Currency.EUR;
			} else if (context.currency) {
				currency = context.currency;
			}

			const headers: any = {
				requestUID: Math.floor(Math.random() * 90000000000000 + 10000000000000),
				'Accept-Language': lang,
				'X-EventId': uuidv4()
			};

			if (
				needToSendCurrencyInHeaders ||
				['FlightsMinPricesInPeriod', 'MinPrices', 'FlightsMinPricesInPeriodRT'].includes(
					operation.operationName
				)
			) {
				headers['X-Currency'] = currency;
			}

			if (token) {
				headers.authorization = token ? `Bearer ${token}` : '';
			}

			operation.setContext(() => ({
				headers: { ...headers, ...getKey() }
			}));

			return forward(operation).map(response => {
				const {
					response: { headers }
				} = operation.getContext();

				if (headers) {
					const currentToken = headers.get('X-Token');

					if (currentToken) {
						token = currentToken;

						Cookie.set(GRAPHQL_KEY, token, {
							expires: !!localStorage.getItem(STAY_LOGGED_IN) ? 90 : undefined,
							sameSite: 'lax',
							secure: process.env.NODE_ENV !== 'development'
						});
					}
				}

				return response;
			});
		});

		const httpLink = new HttpLink({
			uri: GRAPHQL_URL,
			credentials: 'include'
		});

		client = new ApolloClient({
			link: from([
				ignoredErrorsLink,
				errorLink,
				createOrderAffiliateLink,
				getTokenLink,
				paximizerUTMLink,
				webskyAnalyticsLink,
				httpLink
			]),
			cache: new InMemoryCache({ fragmentMatcher })
		});

		WebskyAnalyticsApollo.listenWxViewEvent();
	}

	return client;
};

export const useGraphQLClient = () => {
	if (!GraphQLClientContext) {
		GraphQLClientContext = createContext(createClient());
	}

	return useContext(GraphQLClientContext);
};
